import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Download, Smartphone, Monitor, CheckCircle2 } from "lucide-react";

export default function InstallAppSection() {
  const { isInstallable, isInstalled, installApp } = usePWAInstall();

  if (isInstalled) {
    return (
      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <Card className="p-8 bg-primary/5 border-primary/20">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="bg-primary/10 p-4 rounded-full">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">App Installed!</h2>
              <p className="text-muted-foreground max-w-md">
                You're using Habitualy as an app. Enjoy the full experience with offline support and quick access from your home screen.
              </p>
            </div>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24">
      <div className="container px-4 md:px-6">
        <Card className="p-8 bg-gradient-to-br from-primary/5 via-background to-primary/10 border-primary/20">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1 text-center lg:text-left space-y-4">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-3 py-1 rounded-full text-sm font-medium text-primary">
                <Download className="h-4 w-4" />
                Install App
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">
                Get Habitualy on Your Device
              </h2>
              <p className="text-muted-foreground max-w-lg">
                Install Habitualy for instant access from your home screen. Works offline, loads faster, and feels like a native app — no app store needed.
              </p>
              
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Smartphone className="h-4 w-4" />
                  <span>Mobile</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Monitor className="h-4 w-4" />
                  <span>Desktop</span>
                </div>
              </div>

              {isInstallable ? (
                <Button size="lg" onClick={installApp} className="mt-4">
                  <Download className="h-4 w-4 mr-2" />
                  Install Now
                </Button>
              ) : (
                <div className="space-y-3 pt-4">
                  <p className="text-sm font-medium">How to install:</p>
                  <div className="grid gap-2 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <span className="bg-primary/10 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                      <span><strong>iPhone/iPad:</strong> Tap Share → "Add to Home Screen"</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-primary/10 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                      <span><strong>Android:</strong> Tap menu (⋮) → "Install app" or "Add to Home Screen"</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-primary/10 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                      <span><strong>Desktop:</strong> Click the install icon (⊕) in your browser's address bar</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-48 h-80 bg-gradient-to-b from-card to-muted rounded-3xl border-4 border-foreground/10 shadow-2xl overflow-hidden">
                  <div className="h-6 bg-foreground/5 flex items-center justify-center">
                    <div className="w-16 h-1.5 bg-foreground/10 rounded-full" />
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="h-8 bg-primary/20 rounded-lg flex items-center px-2">
                      <span className="text-xs font-medium">Habitualy</span>
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {Array.from({ length: 28 }).map((_, i) => (
                        <div
                          key={i}
                          className={`aspect-square rounded-sm ${
                            Math.random() > 0.4
                              ? "bg-primary/60"
                              : "bg-muted-foreground/10"
                          }`}
                        />
                      ))}
                    </div>
                    <div className="space-y-1.5 pt-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-6 bg-muted-foreground/10 rounded" />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                  <Download className="h-6 w-6 text-primary-foreground" />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}
